import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { theme } from "../theme";
import { Box, Flex } from "./layout";
import { FaCheck } from "react-icons/fa";

const sizeMap = {
  sm: {
    checkbox: "1",
  },
  md: {
    checkbox: "1.25",
  },
  lg: {
    checkbox: "1.5",
  },
};

const CheckboxWrapper = styled(Flex)(({
  error,
  color,
  size,
  disabled,
  fullWidth,
  labelBold,
  labelFirst,
  checked,
}) => {
  const bgColor = error
    ? theme.colors.danger.main
    : theme.colors[color]?.light || theme.colors.default.dark;

  const checkboxSize = sizeMap[size]?.checkbox || sizeMap.md.checkbox;

  return {
    position: "relative",
    alignItems: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    width: fullWidth ? "100%" : "auto",
    display: fullWidth ? "block" : "inline-block",

    "& label": {
      display: "flex",
      flexDirection: labelFirst ? "row-reverse" : "row",
      width: "100%",
      alignItems: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.75 : 1,
      position: "relative",
      gap: "0.5rem",
    },
    ".checkboxLabel": {
      color: theme.colors.default.dark,
      fontWeight: labelBold ? 600 : 400,
    },
    ".hiddenCheckbox": {
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      zIndex: 2,
      opacity: 0,
      margin: 0,
      cursor: disabled ? "not-allowed" : "pointer",
    },

    ".customCheckbox": {
      width: `${checkboxSize}rem`,
      height: `${checkboxSize}rem`,
      border: `0.0625rem solid ${checked ? bgColor || theme.colors.primary.main : bgColor}`,
      backgroundColor: checked
        ? bgColor || theme.colors.primary.main
        : "transparent",
      transition: "all 0.2s ease",
      position: "relative",
      cursor: disabled ? "not-allowed" : "pointer",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    ".checkMark": {
      display: checked ? "flex" : "none",
      width: "100%",
      height: "100%",
      alignItems: "center",
      justifyContent: "center",
      color: theme.colors.light.light,
      position: "absolute",
      padding: `${checkboxSize / 7}rem`,
    },
    ".errorText": {
      fontSize: "0.75rem",
      color: error ? theme.colors.danger.main : "transparent",
      marginTop: "0.25rem",
    },
  };
});

export const Checkbox = ({
  label,
  color = "default",
  size = "md",
  fullWidth = false,
  labelFirst,
  labelBold,
  error,
  checked,
  disabled,
  onChange,
}) => {
  const [internalChecked, setInternalChecked] = useState(checked);

  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  const handleChange = () => {
    if (!disabled) {
      const newChecked = !internalChecked; // Toggle the checked state
      setInternalChecked(newChecked);
      if (onChange) {
        onChange(newChecked); // Call the passed onChange prop with the new state
      }
    }
  };

  return (
    <CheckboxWrapper
      color={color}
      size={size}
      fullWidth={fullWidth}
      labelBold={labelBold}
      labelFirst={labelFirst}
      error={error}
      checked={internalChecked}
      disabled={disabled}
    >
      <label onClick={handleChange}>
        <input
          type="checkbox"
          className="hiddenCheckbox"
          checked={internalChecked}
          disabled={disabled}
          readOnly // Prevents manual changes; handled by label click
        />
        <Box className={`customCheckbox`}>
          <Box className="checkMark">
            <FaCheck />
          </Box>
        </Box>
        {label && <Box className="checkboxLabel">{label}</Box>}
      </label>
      {error && <span className="errorText">This field has an error.</span>}
    </CheckboxWrapper>
  );
};

Checkbox.defaultProps = {
  label: "",
  color: "default",
  size: "md",
  fullWidth: false,
  labelFirst: false,
  labelBold: false,
  checked: false,
  error: false,
  disabled: false,
  onChange: () => {},
};

Checkbox.propTypes = {
  label: PropTypes.string,
  color: PropTypes.oneOf(["default", "primary", "secondary", "danger"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  fullWidth: PropTypes.bool,
  labelFirst: PropTypes.bool,
  labelBold: PropTypes.bool,
  checked: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Checkbox;
